import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useMoveRaffleTicketsMutation } from "Api/Mutations/Raffle/useMoveRaffleTicketsMutation";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import type { RaffleCompetitionDto } from "Infrastructure/Api/Api";
import { getApiErrorStatus } from "Infrastructure/Api/ApiUtils";
import type { CodeListItem } from "Models/ICodeListDto";
import {
  getRaffleMoveTicketsSchema,
  type RaffleMoveTicketsFormData,
} from "Schemas/raffleMoveTicketsSchema";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Raffle.MoveDialog;

type Props = {
  isOpen: boolean;
  from: RaffleCompetitionDto | null;
  competitions: Array<RaffleCompetitionDto>;
  onClose: () => void;
};

type ContentProps = {
  from: RaffleCompetitionDto;
  targets: Array<RaffleCompetitionDto>;
  onClose: () => void;
};

/** Moves an amount, not picked tickets: they are fungible until the close numbers them (R3). */
const RaffleMoveTicketsDialogContent = ({
  from,
  targets,
  onClose,
}: ContentProps) => {
  const { t } = useResource();
  const { control, handleSubmit } = useForm<RaffleMoveTicketsFormData>({
    resolver: zodResolver(getRaffleMoveTicketsSchema(t, from.myTicketCount)),
    mode: "onChange",
    defaultValues: {
      toRoundPublicID: targets.length === 1 ? targets[0].roundPublicID : "",
      count: from.myTicketCount,
    },
  });

  const {
    mutate: moveTickets,
    isPending,
    isSuccess,
  } = useMoveRaffleTicketsMutation({
    onSuccess: (result) => {
      // Zero moved is not a success: a concurrent refund or move took the tickets.
      if (result.movedCount === 0) {
        toast.info(t(RESOURCES.NothingMoved));
        onClose();
        return;
      }
      toast.success(t(RESOURCES.MovedToast, { count: result.movedCount }));
      onClose();
    },
    onError: (error) => {
      if (getApiErrorStatus(error) === 409) {
        toast.info(t(RESOURCES.CompetitionsChanged));
        onClose();
        return;
      }
      toast.error(t(RESOURCES.MoveError));
    },
  });

  const competitionLabel = (competition: RaffleCompetitionDto) =>
    competition.name ?? t(Resources.Raffle.Page.MainDraw);

  const targetCodeList: Array<CodeListItem> = targets.map((target) => ({
    code: target.roundPublicID,
    name: competitionLabel(target),
  }));

  const onSubmit = (data: RaffleMoveTicketsFormData) =>
    moveTickets({
      fromRoundPublicID: from.roundPublicID,
      toRoundPublicID: data.toRoundPublicID,
      count: data.count,
    });

  return (
    // noValidate: zod validates; the browser's number check would block submit untranslated.
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <DialogTitle>
        {t(RESOURCES.Title)}
        {` — ${competitionLabel(from)}`}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <Dropdown
            control={control}
            name="toRoundPublicID"
            label={t(RESOURCES.TargetLabel)}
            codeList={targetCodeList}
          />
          <FormInput
            control={control}
            name="count"
            label={t(RESOURCES.CountLabel)}
            type="number"
            helperText={t(RESOURCES.CountHelper, { max: from.myTicketCount })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(Resources.Common.Cancel)}</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isPending || isSuccess}
        >
          {t(RESOURCES.Submit)}
        </Button>
      </DialogActions>
    </form>
  );
};

export const RaffleMoveTicketsDialog = ({
  isOpen,
  from,
  competitions,
  onClose,
}: Props) => {
  const targets = competitions.filter(
    (competition) => competition.roundPublicID !== from?.roundPublicID,
  );

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      {from !== null && (
        // Keyed by the source so a re-open starts from fresh defaults.
        <RaffleMoveTicketsDialogContent
          key={from.roundPublicID}
          from={from}
          targets={targets}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
};
